import React, { useEffect, useState } from "react";
import axios from "axios";
import { Truck, Package, ChevronsUpDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fetches the admin-defined default prices for extra services once per session
 * and caches them on the module so multiple mounted editors share the load.
 * Only the admin endpoint is used — if the caller renders the editor for a
 * customer (showPrices=false) we skip the fetch entirely.
 */
let _defaultsPromise = null;
export function fetchExtrasDefaults() {
  if (_defaultsPromise) return _defaultsPromise;
  const token = typeof window !== "undefined" ? localStorage.getItem("mv_admin_token") : null;
  if (!token) return Promise.resolve({});
  _defaultsPromise = axios
    .get(`${API}/admin/settings/extras-defaults`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.data || {})
    .catch(() => ({}));
  return _defaultsPromise;
}

/**
 * Shared editor for the three optional extra services on a shipment/pickup:
 *   1) Servizio sponda        — bool + prezzo
 *   2) Camion completo        — bool + metri di pianale (decimale) + prezzo
 *   3) Furgone completo       — bool + numero mezzi (intero) + prezzo
 *
 * `value` is the ExtraServices dict shape returned by the backend.
 * `onChange` receives the entire updated dict.
 *
 * `showPrices` — pass false on customer-facing forms if you want to hide the
 * price fields (customer doesn't set prices, admin does). Default true.
 * When true (admin mode) the editor also fetches the admin-configured default
 * prices and auto-applies them the first time a service is toggled ON with an
 * empty/zero cost.
 */
export default function ExtraServicesEditor({ value, onChange, showPrices = true, testIdPrefix = "extras" }) {
  const v = value || {};
  const [defaults, setDefaults] = useState({});

  useEffect(() => {
    if (!showPrices) return;
    fetchExtrasDefaults().then(setDefaults);
  }, [showPrices]);

  const patch = (delta) => onChange({ ...v, ...delta });

  const toggle = (flagKey, costKey, defaultCost) => (e) => {
    const checked = e.target.checked;
    const next = { ...v, [flagKey]: checked };
    if (checked && showPrices && defaultCost && !(parseFloat(v[costKey]) > 0)) {
      next[costKey] = defaultCost;
    }
    onChange(next);
  };

  return (
    <div className="border border-zinc-200 bg-zinc-50 rounded-sm p-3 space-y-3" data-testid={`${testIdPrefix}-block`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Servizi Extra (opzionali)</div>

      {/* Sponda */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={!!v.sponda}
            onChange={toggle("sponda", "sponda_cost", defaults.sponda_cost)}
            className="h-4 w-4 accent-zinc-800"
            data-testid={`${testIdPrefix}-sponda-chk`}
          />
          <ChevronsUpDown size={14} className="text-zinc-500" />
          <span>Servizio sponda</span>
        </label>
        {showPrices && v.sponda && (
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder="Prezzo €"
            value={v.sponda_cost ?? ""}
            onChange={(e) => patch({ sponda_cost: parseFloat(e.target.value) || 0 })}
            className="w-28 px-2 py-1 border border-zinc-300 rounded-sm text-sm"
            data-testid={`${testIdPrefix}-sponda-cost`}
          />
        )}
      </div>

      {/* Camion completo */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={!!v.camion_completo}
            onChange={toggle("camion_completo", "camion_cost", defaults.camion_cost)}
            className="h-4 w-4 accent-zinc-800"
            data-testid={`${testIdPrefix}-camion-chk`}
          />
          <Truck size={14} className="text-zinc-500" />
          <span>Camion completo</span>
        </label>
        {v.camion_completo && (
          <>
            <input
              type="number"
              step="0.1"
              min="0"
              placeholder="Metri pianale"
              value={v.camion_metri ?? ""}
              onChange={(e) => patch({ camion_metri: parseFloat(e.target.value) || 0 })}
              className="w-32 px-2 py-1 border border-zinc-300 rounded-sm text-sm"
              data-testid={`${testIdPrefix}-camion-metri`}
            />
            {showPrices && (
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Prezzo €"
                value={v.camion_cost ?? ""}
                onChange={(e) => patch({ camion_cost: parseFloat(e.target.value) || 0 })}
                className="w-28 px-2 py-1 border border-zinc-300 rounded-sm text-sm"
                data-testid={`${testIdPrefix}-camion-cost`}
              />
            )}
          </>
        )}
      </div>

      {/* Furgone completo */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm select-none">
          <input
            type="checkbox"
            checked={!!v.furgone_completo}
            onChange={toggle("furgone_completo", "furgone_cost", defaults.furgone_cost)}
            className="h-4 w-4 accent-zinc-800"
            data-testid={`${testIdPrefix}-furgone-chk`}
          />
          <Package size={14} className="text-zinc-500" />
          <span>Furgone completo</span>
        </label>
        {v.furgone_completo && (
          <>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="N. mezzi"
              value={v.furgone_num_mezzi ?? ""}
              onChange={(e) => patch({ furgone_num_mezzi: parseInt(e.target.value, 10) || 0 })}
              className="w-24 px-2 py-1 border border-zinc-300 rounded-sm text-sm"
              data-testid={`${testIdPrefix}-furgone-num`}
            />
            {showPrices && (
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Prezzo €"
                value={v.furgone_cost ?? ""}
                onChange={(e) => patch({ furgone_cost: parseFloat(e.target.value) || 0 })}
                className="w-28 px-2 py-1 border border-zinc-300 rounded-sm text-sm"
                data-testid={`${testIdPrefix}-furgone-cost`}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Read-only badge list, used to show enabled extras on job cards
 * (customer dashboard, driver PWA, admin expanded row).
 */
export function ExtraServicesBadges({ value, item, className = "" }) {
  const v = value || {};
  const items = [];
  if (item?.urgency === "urgente" && parseFloat(item.urgency_cost) > 0) {
    items.push({ label: "Urgenza", cost: parseFloat(item.urgency_cost), urgent: true });
  }
  if (v.sponda) items.push({ label: "Sponda", cost: v.sponda_cost });
  if (v.camion_completo) {
    const metri = v.camion_metri ? ` ${v.camion_metri}m` : "";
    items.push({ label: `Camion completo${metri}`, cost: v.camion_cost });
  }
  if (v.furgone_completo) {
    const n = v.furgone_num_mezzi ? ` x${v.furgone_num_mezzi}` : "";
    items.push({ label: `Furgone completo${n}`, cost: v.furgone_cost });
  }
  if (!items.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} data-testid="extras-badges">
      {items.map((it, i) => (
        <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${it.urgent ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-800"}`} data-testid={it.urgent ? "urgency-cost-badge" : undefined}>
          {it.label}
          {it.cost > 0 && <span className="opacity-80">({it.cost.toFixed(2)}€)</span>}
        </span>
      ))}
    </div>
  );
}
